import gsap from 'gsap';
import type { Manifest, Work } from '../manifest';
import { t, pick, onLangChange } from '../i18n';
import { clearWorkUrl, updateOgTags } from '../seo';
import { shareLink } from '../share';
import { neighbors } from './nav';

const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

export class DetailView {
  private el: HTMLDivElement;
  private img: HTMLImageElement;
  private metaEl: HTMLDivElement;
  private titleEl: HTMLHeadingElement;
  private descPanel: HTMLElement;
  private descEl: HTMLParagraphElement;
  private infoEl: HTMLSpanElement;
  private prevBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private shareBtn: HTMLButtonElement;
  private current: Work | null = null;
  private onCloseCb: (() => void | Promise<void>) | null = null;
  private closing: Promise<void> | null = null;
  private openTl: gsap.core.Timeline | null = null;

  constructor(private manifest: Manifest) {
    this.el = document.createElement('div');
    this.el.id = 'detail';
    this.el.innerHTML = `
      <button class="close" aria-label="close">×</button>
      <img alt="" />
      <aside class="detail-desc"><p></p></aside>
      <div class="meta">
        <h1></h1>
        <span class="info"></span>
        <nav>
          <button type="button" class="share"></button>
          <button type="button" class="prev"></button>
          <button type="button" class="next"></button>
        </nav>
      </div>`;
    document.body.appendChild(this.el);

    this.img = this.el.querySelector('img')!;
    this.metaEl = this.el.querySelector('.meta')!;
    this.titleEl = this.el.querySelector('h1')!;
    this.descPanel = this.el.querySelector('.detail-desc')!;
    this.descEl = this.descPanel.querySelector('p')!;
    this.infoEl = this.el.querySelector('.info')!;
    this.prevBtn = this.el.querySelector('.prev')!;
    this.nextBtn = this.el.querySelector('.next')!;
    this.shareBtn = this.el.querySelector('.share')!;

    this.el.querySelector('.close')!.addEventListener('click', () => void this.close());
    this.shareBtn.addEventListener('click', () => {
      if (!this.current) return;
      void shareLink({ work: this.current.id }, pick(this.current.title) || 'MOSER');
    });
    this.prevBtn.addEventListener('click', () => this.step(-1));
    this.nextBtn.addEventListener('click', () => this.step(1));
    window.addEventListener('keydown', (e) => {
      if (!this.current) return;
      if (e.key === 'Escape') void this.close();
      if (e.key === 'ArrowRight') this.step(1);
      if (e.key === 'ArrowLeft') this.step(-1);
    });
    onLangChange(() => {
      if (this.current) this.render();
    });
  }

  get isOpen(): boolean {
    return this.current !== null;
  }

  open(work: Work, onClose: () => void | Promise<void>): void {
    void this.closing;
    this.openTl?.kill();
    gsap.killTweensOf([this.el, this.img, this.metaEl, this.descPanel]);

    this.current = work;
    this.onCloseCb = onClose;
    this.render();
    updateOgTags(work);
    this.el.classList.add('open');

    if (REDUCED()) {
      gsap.set(this.el, { opacity: 1 });
      gsap.set(this.img, { scale: 1 });
      gsap.set([this.metaEl, this.descPanel], { opacity: 1, y: 0 });
      return;
    }

    gsap.set(this.el, { opacity: 0 });
    gsap.set(this.img, { scale: 0.96, transformOrigin: '50% 50%' });
    gsap.set([this.metaEl, this.descPanel], { opacity: 0, y: 16 });

    this.openTl = gsap.timeline();
    this.openTl.to(this.el, { opacity: 1, duration: 0.35, ease: 'power2.out' }, 0);
    this.openTl.to(this.img, { scale: 1, duration: 0.5, ease: 'power2.out' }, 0.05);
    this.openTl.to(this.metaEl, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.12);
    this.openTl.to(this.descPanel, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.18);
  }

  close(): Promise<void> {
    if (!this.current) return Promise.resolve();
    if (this.closing) return this.closing;

    const cb = this.onCloseCb;
    this.onCloseCb = null;
    this.openTl?.kill();
    gsap.killTweensOf([this.el, this.img, this.metaEl, this.descPanel]);

    this.closing = (async () => {
      if (!REDUCED()) {
        await gsap.timeline()
          .to([this.metaEl, this.descPanel], { opacity: 0, y: 12, duration: 0.25, ease: 'power2.in' }, 0)
          .to(this.img, { scale: 0.96, duration: 0.3, ease: 'power2.in' }, 0.05)
          .to(this.el, { opacity: 0, duration: 0.3, ease: 'power2.in' }, 0.1);
      }

      this.el.classList.remove('open');
      this.current = null;
      clearWorkUrl();
      updateOgTags();
      gsap.set([this.el, this.img, this.metaEl, this.descPanel], { clearProps: 'all' });

      if (cb) await cb();
      this.closing = null;
    })();

    return this.closing;
  }

  private step(dir: 1 | -1): void {
    if (!this.current) return;
    const { prev, next } = neighbors(this.manifest.works, this.current.id);
    this.current = dir === 1 ? next : prev;
    this.render();
  }

  private render(): void {
    const w = this.current!;
    const file = w.ext === 'gif' ? `${w.id}.gif` : `${w.id}-l.webp`;
    this.img.src = `${import.meta.env.BASE_URL}textures/${file}`;
    this.img.alt = pick(w.title) || t('untitled');
    this.titleEl.textContent = pick(w.title) || t('untitled');
    const desc = pick(w.description, '');
    this.descEl.textContent = desc;
    this.descPanel.classList.toggle('has-desc', desc.trim().length > 0);
    this.descPanel.style.setProperty('--accent', w.palette[0] ?? '#888888');
    this.infoEl.textContent = `${w.year} · ${w.cluster}`;
    this.shareBtn.textContent = t('share');
    this.prevBtn.textContent = `← ${t('prev')}`;
    this.nextBtn.textContent = `${t('next')} →`;
  }
}
